import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import { api, APIError } from "~/lib/api";
import { useMe } from "~/lib/auth";
import { AppShell } from "~/components/AppShell";
import { Button } from "~/components/ui/Button";
import { useToast } from "~/components/ui/Toast";

export const Route = createFileRoute("/_authed/account/password")({
  component: ChangePasswordPage,
});

const MIN_LEN = 8;

function ChangePasswordPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const isFirstSet = me ? !me.has_password : false;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showAll, setShowAll] = useState(false);

  const [currentError, setCurrentError] = useState<string | null>(null);

  const validation = useMemo(() => {
    if (!isFirstSet && !current) return { ok: false, hint: "Enter your current password." };
    if (next.length < MIN_LEN)
      return { ok: false, hint: `Password must be at least ${MIN_LEN} characters.` };
    if (!isFirstSet && next === current)
      return { ok: false, hint: "New password must differ from the current one." };
    if (confirm !== next) return { ok: false, hint: "Passwords don't match." };
    return { ok: true as const, hint: null };
  }, [current, next, confirm, isFirstSet]);

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: async () => {
      toast.success(isFirstSet ? "Password set." : "Password updated.");
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate({ to: "/account" });
    },
    onError: (err) => {
      if (!isFirstSet && err instanceof APIError && err.status === 400) {
        setCurrentError(err.message || "Current password is incorrect.");
        return;
      }
      toast.error(err instanceof Error ? err.message : "Couldn't save the password.");
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validation.ok || change.isPending) return;
    setCurrentError(null);
    change.mutate();
  }

  if (!me) return <AppShell back={{ to: "/account", label: "Settings" }} showTabBar={false}><div /></AppShell>;

  return (
    <AppShell back={{ to: "/account", label: "Settings" }} showTabBar={false}>
      <div className="max-w-md mx-auto pt-4 sm:pt-6 lg:pt-10">
        <header className="pb-6">
          <h1 className="text-largeTitle" style={{ color: "var(--label-primary)" }}>
            {isFirstSet ? "Set a password" : "Change password"}
          </h1>
          <div className="text-subhead mt-1" style={{ color: "var(--label-secondary)" }}>
            {isFirstSet
              ? `Add a password so you can sign in without an email code. Use at least ${MIN_LEN} characters.`
              : `Use at least ${MIN_LEN} characters. You'll stay signed in on this device.`}
          </div>
        </header>

        <form onSubmit={onSubmit} className="space-y-4">
          {!isFirstSet && (
            <PasswordField
              id="current"
              label="Current password"
              value={current}
              onChange={(v) => {
                setCurrent(v);
                if (currentError) setCurrentError(null);
              }}
              autoComplete="current-password"
              error={currentError ?? undefined}
              visible={showAll}
              onToggleVisible={() => setShowAll((s) => !s)}
              autoFocus
            />
          )}
          <PasswordField
            id="next"
            label={isFirstSet ? "Password" : "New password"}
            value={next}
            onChange={setNext}
            autoComplete="new-password"
            visible={showAll}
            onToggleVisible={() => setShowAll((s) => !s)}
            autoFocus={isFirstSet}
            hint={
              next.length > 0 && next.length < MIN_LEN
                ? `${MIN_LEN - next.length} more character${MIN_LEN - next.length === 1 ? "" : "s"}`
                : undefined
            }
          />
          <PasswordField
            id="confirm"
            label={isFirstSet ? "Confirm password" : "Confirm new password"}
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            visible={showAll}
            onToggleVisible={() => setShowAll((s) => !s)}
            error={
              confirm.length > 0 && confirm !== next ? "Passwords don't match." : undefined
            }
          />

          <div className="pt-1 flex flex-col gap-2">
            <Button
              type="submit"
              size="lg"
              loading={change.isPending}
              disabled={!validation.ok || change.isPending}
              leadingIcon={<Lock size={18} />}
              className="w-full"
            >
              {isFirstSet ? "Set password" : "Update password"}
            </Button>
            {!change.isPending && validation.hint && (isFirstSet ? next : current) && (
              <p
                className="text-footnote text-center"
                style={{ color: "var(--label-tertiary)" }}
              >
                {validation.hint}
              </p>
            )}
          </div>
        </form>
      </div>
    </AppShell>
  );
}

type PasswordFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  autoComplete?: string;
  error?: string;
  hint?: string;
  visible: boolean;
  onToggleVisible: () => void;
  autoFocus?: boolean;
};

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  error,
  hint,
  visible,
  onToggleVisible,
  autoFocus,
}: PasswordFieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="text-footnote uppercase tracking-[0.04em] block pb-1.5"
        style={{ color: "var(--label-secondary)", fontWeight: 600 }}
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className="w-full text-body focus:outline-none"
          style={{
            background: "var(--fill-tertiary)",
            border: `0.5px solid ${error ? "var(--system-red)" : "var(--separator)"}`,
            color: "var(--label-primary)",
            height: 50,
            paddingLeft: 16,
            paddingRight: 44,
            borderRadius: 12,
          }}
          aria-invalid={!!error}
          aria-describedby={error || hint ? `${id}-msg` : undefined}
        />
        <button
          type="button"
          onClick={onToggleVisible}
          aria-label={visible ? "Hide passwords" : "Show passwords"}
          className="absolute top-1/2 -translate-y-1/2 right-2 grid place-items-center transition-opacity active:opacity-60"
          style={{
            width: 32,
            height: 32,
            color: "var(--label-secondary)",
            borderRadius: 8,
          }}
          tabIndex={-1}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {(error || hint) && (
        <p
          id={`${id}-msg`}
          className="text-footnote mt-1.5"
          style={{ color: error ? "var(--system-red)" : "var(--label-tertiary)" }}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
